<!-- Detail MO: tampilkan informasi + aksi print pallet -->
<template>
  <v-container fluid class="pa-0 mo-bg" style="min-height: 87vh">
    <v-row align="center" justify="center" class="ma-0 pa-0">
      <v-col cols="12" sm="10" md="8" lg="7">
        <v-sheet
          class="mo-panel neo-surface elevation-3 rounded-xl mx-auto"
          max-width="860"
        >
          <v-toolbar flat dense class="mo-toolbar">
            <v-btn icon small class="white--text mr-1" @click="goBack">
              <v-icon color="white" small>mdi-arrow-left</v-icon>
            </v-btn>
            <v-toolbar-title class="white--text d-flex align-center">
              <v-icon left color="white" class="mr-2">mdi-factory</v-icon>
              Detail Manufacturing Order
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn
              icon
              small
              class="white--text"
              :disabled="loading"
              @click="refreshData"
            >
              <v-icon color="white" small :class="{ spin: loading }"
                >mdi-refresh</v-icon
              >
            </v-btn>
          </v-toolbar>
          <!-- Linear loader with message -->
          <div v-if="loading" class="d-flex align-center px-5 pt-2">
            <v-progress-linear
              indeterminate
              color="primary"
              height="4"
              rounded
              style="flex: 1"
            />
            <span class="mo-wait ml-4">Please wait...</span>
          </div>

          <!-- Decorative soft canvas background -->
          <div class="position-relative">
            <canvas ref="bgCanvas" class="bg-canvas"></canvas>
            <div class="pa-5 pt-6 px-6 px-sm-8 neo-surface">
              <!-- Skeleton loader when loading/initial -->
              <v-row dense v-if="loading || !detailData">
                <v-col cols="12">
                  <v-skeleton-loader
                    class="mb-3"
                    type="heading"
                  ></v-skeleton-loader>
                </v-col>
                <v-col cols="12">
                  <v-skeleton-loader
                    class="mb-4"
                    type="text"
                  ></v-skeleton-loader>
                </v-col>
                <v-col cols="6">
                  <v-skeleton-loader
                    class="mb-3"
                    type="text"
                  ></v-skeleton-loader>
                </v-col>
                <v-col cols="6">
                  <v-skeleton-loader
                    class="mb-3"
                    type="text"
                  ></v-skeleton-loader>
                </v-col>
                <v-col cols="12">
                  <v-skeleton-loader
                    class="mb-4"
                    type="text"
                  ></v-skeleton-loader>
                </v-col>
                <v-col cols="12">
                  <v-skeleton-loader
                    class="mb-3"
                    type="heading"
                  ></v-skeleton-loader>
                  <v-skeleton-loader
                    class="mb-2"
                    type="table-thead"
                  ></v-skeleton-loader>
                  <v-skeleton-loader
                    class="mb-1"
                    type="table-row"
                  ></v-skeleton-loader>
                  <v-skeleton-loader
                    class="mb-1"
                    type="table-row"
                  ></v-skeleton-loader>
                </v-col>
              </v-row>

              <!-- Detail grid -->
              <v-row dense v-else>
                <!-- Header: Company centered, Status at top-right -->
                <v-col cols="12">
                  <div class="mo-header value emphasis-1">
                    {{
                      (detailData &&
                        detailData.company &&
                        detailData.company.name) ||
                      '-'
                    }}
                  </div>
                </v-col>

                <v-col cols="7" md="7">
                  <div class="label with-icon">
                    <v-icon small class="label-icon">mdi-pound-box</v-icon>
                    <span>Nomor MO</span>
                  </div>
                  <div class="value emphasis-2">
                    {{ (detailData && detailData.name) || '-' }}
                  </div>
                </v-col>

                <v-col cols="7" md="7">
                  <div class="label with-icon">
                    <v-icon small class="label-icon">mdi-barcode</v-icon>
                    <span>Kode Produksi</span>
                  </div>
                  <div class="value emphasis-2">
                    {{ (detailData && detailData.code_production) || '-' }}
                  </div>
                </v-col>
                <v-col cols="5" md="5">
                  <div class="label with-icon">
                    <v-icon small class="label-icon">mdi-list-status</v-icon>
                    <span>Status MO</span>
                  </div>
                  <div class="value">
                    <v-chip
                      small
                      label
                      class="white--text text-uppercase"
                      :color="statusInfo.color"
                    >
                      {{ statusInfo.label }}
                    </v-chip>
                  </div>
                </v-col>

                <v-col cols="7" md="7">
                  <div class="label with-icon">
                    <v-icon small class="label-icon">mdi-tag-outline</v-icon>
                    <span>Kode Produk</span>
                  </div>
                  <div class="value">
                    {{
                      (detailData &&
                        detailData.product &&
                        detailData.product.product_code) ||
                      '-'
                    }}
                  </div>
                </v-col>
                <v-col cols="5" md="5">
                  <div class="label with-icon">
                    <v-icon small class="label-icon"
                      >mdi-package-variant</v-icon
                    >
                    <span>Nama Produk</span>
                  </div>
                  <div class="value uppercase">
                    {{
                      (detailData &&
                        detailData.product &&
                        detailData.product.name) ||
                      '-'
                    }}
                  </div>
                </v-col>
                <!-- Expandable printed passet list under product info -->
                <v-col cols="12">
                  <v-slide-y-transition mode="out-in">
                    <div v-if="showDetailList" class="mt-2 expand-box">
                      <v-text-field
                        class="neo-input search-input rounded-xl mb-2"
                        v-model="searchQuery"
                        placeholder="Search"
                        prepend-inner-icon="mdi-magnify"
                        append-outer-icon="mdi-qrcode-scan"
                        @click:append-outer="openSearchScanDialog"
                        clearable
                        outlined
                        dense
                      />
                      <template v-if="listLoading">
                        <v-skeleton-loader
                          v-for="i in 4"
                          :key="'skl-' + i"
                          type="list-item-two-line"
                          class="mb-2"
                        />
                      </template>
                      <template v-else>
                        <div
                          v-if="filteredPrintedList.length === 0"
                          class="grey--text text--darken-1 pl-1"
                        >
                          Tidak ada data
                        </div>
                        <v-card
                          v-for="(row, idx) in filteredPrintedList"
                          :key="'printed-' + idx"
                          class="passet-card rounded-lg elevation-1 mb-2"
                          :class="
                            row.status === 'confirmed'
                              ? 'ok'
                              : row.status === 'printed'
                              ? 'warn'
                              : 'bad'
                          "
                        >
                          <v-card-text class="py-3">
                            <div
                              class="d-flex align-center justify-space-between"
                            >
                              <div class="code">{{ row.code }}</div>
                              <div class="d-flex align-center">
                                <v-tooltip bottom>
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                      icon
                                      small
                                      class="mr-2"
                                      v-bind="attrs"
                                      v-on="on"
                                      @click="openReprintDialog(row)"
                                    >
                                      <v-icon small>mdi-printer</v-icon>
                                    </v-btn>
                                  </template>
                                  <span>Print Ulang</span>
                                </v-tooltip>
                                <v-icon
                                  v-if="row.status !== 'printed'"
                                  small
                                  :class="
                                    row.status === 'confirmed'
                                      ? 'ok-icon'
                                      : 'bad-icon'
                                  "
                                >
                                  {{
                                    row.status === 'confirmed'
                                      ? 'mdi-check-bold'
                                      : 'mdi-close-thick'
                                  }}
                                </v-icon>
                              </div>
                            </div>
                            <div class="meta mt-1">
                              Printed at : {{ row.printed_at }}
                            </div>
                            <div class="meta">
                              No. Lot : {{ row.lot_no }}
                            </div>
                            <div class="meta">
                              Pallet : {{ row.pallet_config }} susun
                            </div>
                            <div class="meta">
                              Produksi : {{ row.qty_production }} Dus
                            </div>
                            <div class="meta">
                              Sisa : {{ row.qty_remain }} Dus
                            </div>
                            <div class="count-badge">
                              COUNT: {{ row.count }}
                            </div>
                          </v-card-text>
                        </v-card>
                      </template>
                    </div>
                  </v-slide-y-transition>
                </v-col>
                <v-col cols="7" md="7" v-show="!showDetailList">
                  <div class="label with-icon">
                    <v-icon small class="label-icon">mdi-cog</v-icon>
                    <span>Nama Mesin</span>
                  </div>
                  <div class="value uppercase">
                    {{
                      (detailData &&
                        detailData.machine &&
                        detailData.machine.name) ||
                      '-'
                    }}
                  </div>
                </v-col>
                <v-col cols="5" md="5" v-show="!showDetailList">
                  <div class="label with-icon">
                    <v-icon small class="label-icon">mdi-clock-outline</v-icon>
                    <span>Shift</span>
                  </div>
                  <div class="value uppercase">
                    {{
                      twoWords(
                        detailData && detailData.shift && detailData.shift.name
                      )
                    }}
                  </div>
                </v-col>
                <v-col cols="12" v-show="!showDetailList">
                  <div class="label with-icon">
                    <v-icon small class="label-icon">mdi-calendar</v-icon>
                    <span>Tanggal Produksi</span>
                  </div>
                  <div class="value">
                    {{
                      formatDateTime(
                        detailData && detailData.date_planned_start
                      )
                    }}
                  </div>
                </v-col>
                <v-col cols="7" md="7" v-show="!showDetailList">
                  <div class="label with-icon">
                    <v-icon small class="label-icon">mdi-calculator</v-icon>
                    <span>Estimasi Produksi</span>
                  </div>
                  <div class="value">
                    {{
                      detailData && detailData.target_produksi
                        ? formatNumber(detailData.target_produksi)
                        : '0'
                    }}
                    DUS
                  </div>
                </v-col>
                <v-col cols="5" md="5" v-show="!showDetailList">
                  <div class="label with-icon">
                    <v-icon small class="label-icon">mdi-palette</v-icon>
                    <span>Total Pallet</span>
                  </div>
                  <div class="value">{{ formatNumber(totalPalletOut) }}</div>
                </v-col>
                <v-col cols="12" v-show="!showDetailList">
                  <div class="label with-icon">
                    <v-icon small class="label-icon">mdi-table</v-icon>
                    <span>Summary Pallet</span>
                  </div>
                  <div class="summary-wrap mt-1">
                    <v-simple-table dense class="summary-table">
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th class="text-left">Palet</th>
                            <th class="text-right">Qty</th>
                            <th class="text-right">Total Dus</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(r, i) in summaryPallet" :key="'sum-' + i">
                            <td>{{ r.pallet_type }}</td>
                            <td class="text-right">
                              {{ formatNumber(r.total_pallet) }}
                            </td>
                            <td class="text-right">
                              {{ formatNumber(r.total_produksi) }}
                            </td>
                          </tr>
                          <tr v-if="summaryPallet.length === 0">
                            <td
                              colspan="3"
                              class="grey--text text--darken-1 text-center"
                            >
                              Tidak ada data
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </div>
                </v-col>
              </v-row>

              <!-- Actions -->
              <v-row class="mt-6 neo-surface pa-3 rounded-lg" dense>
                <v-col cols="12" md="6" class="mt-1">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        block
                        class="neo-btn btn-ellipsis"
                        large
                        @click="toggleDetailList"
                      >
                        <v-icon left>{{
                          showDetailList
                            ? 'mdi-close'
                            : 'mdi-format-list-bulleted'
                        }}</v-icon>
                        {{ showDetailList ? 'Close Detail' : 'Open Detail' }}
                      </v-btn>
                    </template>
                    <span>{{
                      showDetailList ? 'Close Detail' : 'Open Detail'
                    }}</span>
                  </v-tooltip>
                </v-col>
                <v-col cols="12" md="6" class="mt-1">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        block
                        class="neo-btn btn-ellipsis"
                        large
                        @click="openPrintDialog"
                      >
                        <v-icon left>mdi-printer</v-icon>
                        Print Passet
                      </v-btn>
                    </template>
                    <span>Print Passet</span>
                  </v-tooltip>
                </v-col>
                <v-col cols="12" md="6" class="mt-1">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        block
                        class="neo-btn btn-ellipsis"
                        large
                        @click="confirmAllPasset"
                      >
                        <v-icon left>mdi-check-all</v-icon>
                        Confirm Passet
                      </v-btn>
                    </template>
                    <span>Update status semua passet menjadi confirmed</span>
                  </v-tooltip>
                </v-col>
                <v-col cols="12" md="6" class="mt-1">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        block
                        class="neo-btn btn-ellipsis"
                        large
                        @click="openRejectScanDialog"
                      >
                        <v-icon left>mdi-qrcode-scan</v-icon>
                        Reject Passet
                      </v-btn>
                    </template>
                    <span>Scan Passet Tidak Terpakai</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </div>
          </div>
        </v-sheet>
      </v-col>
    </v-row>

    <!-- Dialog Print Pallet -->
    <v-dialog
      v-model="dialogPrint"
      max-width="520"
      persistent
      scrollable
      overlay-opacity="0.15"
    >
      <v-card class="rounded-xl elevation-3 neo-surface">
        <v-toolbar dense flat class="mo-toolbar">
          <v-toolbar-title class="white--text d-flex align-center">
            <v-icon left color="white" class="mr-2">mdi-palette-swatch</v-icon>
            Pilih Jenis Palet
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon small @click="dialogPrint = false" class="white--text">
            <v-icon small color="white">mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-5 neo-surface">
          <v-alert
            v-if="printFormError"
            dense
            outlined
            type="error"
            :icon="'mdi-alert-outline'"
            class="neo-alert mb-3"
          >
            {{ printFormError }}
          </v-alert>
          <v-row dense>
            <v-col cols="12">
              <v-select
                class="neo-input"
                v-model="printForm.type"
                :items="palletTypes"
                label="Pilih Palet"
                item-text="name"
                item-value="id"
                outlined
                return-object
                dense
                @change="onPalletTypeChange"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                class="neo-input"
                v-model.number="printForm.qty_load"
                label="Jumlah Muatan"
                type="number"
                min="1"
                outlined
                dense
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                class="neo-input"
                v-model.number="printForm.qty_print"
                label="Qty Print"
                type="number"
                min="1"
                outlined
                dense
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4 neo-surface">
          <v-spacer />
          <v-btn class="neo-btn width-80" @click="confirmPrint">
            <v-icon left>mdi-printer</v-icon>
            Print
          </v-btn>
          <v-btn text @click="closeDialog">Batal</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <production-scanner
      v-model="dialogRejectScan"
      title="Scan Reject Passet"
      input-label="Scan/Tempel QR Code"
      :action-label="'Reject'"
      :auto-close-on-scan="false"
      :auto-emit-on-scan="false"
      @scanned="handleRejectScanned"
    />
    <production-scanner
      v-model="dialogSearchScan"
      title="Cari Nomor Passet"
      input-label="Scan/Tempel Keyword"
      :action-label="'Cari Passet'"
      :auto-close-on-scan="true"
      :auto-emit-on-scan="true"
      @scanned="handleSearchScanned"
    />
    <v-dialog
      v-model="dialogReprint"
      max-width="520"
      persistent
      scrollable
      overlay-opacity="0.15"
    >
      <v-card class="rounded-xl elevation-3 neo-surface">
        <v-toolbar dense flat class="mo-toolbar">
          <v-toolbar-title class="white--text d-flex align-center">
            <v-icon left color="white" class="mr-2">mdi-printer</v-icon>
            Reprint Palet
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon small @click="dialogReprint = false" class="white--text">
            <v-icon small color="white">mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-5 neo-surface">
          <v-alert
            v-if="reprintFormError"
            dense
            outlined
            type="warning"
            :icon="'mdi-alert-circle-outline'"
            class="neo-alert mb-3"
          >
            {{ reprintFormError }}
          </v-alert>
          <v-row dense>
            <v-col cols="12">
              <v-text-field
                class="neo-input"
                v-model.number="reprintForm.qty_print"
                label="Qty Print"
                type="number"
                min="1"
                outlined
                dense
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4 neo-surface">
          <v-spacer />
          <v-btn class="neo-btn" @click="confirmReprint">
            <v-icon left>mdi-printer</v-icon>
            Print
          </v-btn>
          <v-btn text @click="dialogReprint = false">Batal</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'
import buildType from '../../../services/buildType'
import { mapGetters } from 'vuex'
export default {
  name: 'ManufactureDetail',
  components: {
    ProductionScanner: () =>
      import('../../home/components/ProductionScanner.vue')
  },
  data() {
    return {
      build: process.env.VUE_APP_BUILD_TYPE,
      odooApi: buildType.apiURL('odoo'),
      passedApi: buildType.apiURL('passed'),
      detailData: null,
      loading: false,
      dialogPrint: false,
      palletTypes: [],
      palletLayers: [],
      showDetailList: false,
      listLoading: false,
      searchQuery: '',
      printedList: [],
      dialogRejectScan: false,
      dialogSearchScan: false,
      dialogReprint: false,
      reprintForm: { id: null, qty_print: null },
      reprintFormError: '',
      printFormError: '',
      printForm: { type: null, qty_load: null, qty_print: null },
      summaryPallet: []
    }
  },
  computed: {
    ...mapGetters(['getUserProfile']),
    statusInfo: function () {
      var raw = (this.detailData && this.detailData.state) || ''
      var s = String(raw).toLowerCase()
      // Only three MO states: draft, confirmed, done
      if (s === 'draft') return { label: 'Draft', color: 'grey darken-1' }
      if (s === 'confirmed') return { label: 'Confirmed', color: 'blue' }
      if (s === 'done') return { label: 'Done', color: 'green' }
      return { label: raw || '-', color: 'grey' }
    },
    filteredPrintedList: function () {
      var q = (this.searchQuery || '').toString().toLowerCase()
      if (!q) return this.printedList
      return this.printedList.filter(function (x) {
        var s1 = (x.code || '').toString().toLowerCase()
        var s2 = (x.printed_at || '').toString().toLowerCase()
        return s1.indexOf(q) !== -1 || s2.indexOf(q) !== -1
      })
    },
    totalPalletOut: function () {
      var total = 0
      var arr = this.summaryPallet || []
      for (var i = 0; i < arr.length; i++) {
        var q = Number(arr[i].total_pallet) || 0
        total += q
      }
      return total
    }
  },
  mounted() {
    this.getDetailMO()
    this.$nextTick(() => this.drawBackground())
    window.addEventListener('resize', this.drawBackground)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.drawBackground)
  },
  methods: {
    async showConfirmation(title, text, btnYes, btnNo) {
      if (!this.$swal) {
        console.error('SweetAlert instance not available')
        return
      }

      return this.$swal.fire({
        title: title,
        text: text,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: btnYes,
        cancelButtonText: btnNo,
        focusCancel: true
      })
    },
    onPalletTypeChange(val) {
      // Find selected pallet type item and set default qty_load
      var it = null
      for (var i = 0; i < (this.palletTypes || []).length; i++) {
        if (this.palletTypes[i] && this.palletTypes[i].id === val.id) {
          it = this.palletTypes[i]
          break
        }
      }
      var qty = 0
      if (it) {
        var a = parseInt(it.palletizer_qty_per_layer, 10)
        var b = parseInt(it.palletizer_total_layer, 10)
        if (!isNaN(a) && !isNaN(b)) qty = a * b
      }
      // Fallback: search in raw palletLayers by id
      if (!qty && this.palletLayers && this.palletLayers.length) {
        for (var j = 0; j < this.palletLayers.length; j++) {
          var pl = this.palletLayers[j]
          var pid = pl && pl.id != null ? pl.id : pl && pl.name
          if (pid === val.id) {
            var a2 = parseInt(pl.palletizer_qty_per_layer, 10)
            var b2 = parseInt(pl.palletizer_total_layer, 10)
            if (!isNaN(a2) && !isNaN(b2)) {
              qty = a2 * b2
              break
            }
          }
        }
      }
      if (qty > 0) this.printForm.qty_load = qty
    },
    perPallet: function (pl) {
      if (!pl) return 0
      var a = parseInt(pl.palletizer_qty_per_layer, 10)
      var b = parseInt(pl.palletizer_total_layer, 10)
      if (isNaN(a) || isNaN(b)) return 0
      return a * b
    },
    formatNumber: function (n) {
      if (n == null || isNaN(n)) return '0'
      try {
        return Number(n).toLocaleString('id-ID')
      } catch (e) {
        return String(n)
      }
    },
    formatDateTime: function (val) {
      if (!val) return '-'
      var d = new Date(val)
      if (isNaN(d.getTime())) {
        var s = String(val).replace(' ', 'T')
        d = new Date(s)
      }
      if (isNaN(d.getTime())) {
        var m = String(val).match(
          /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/
        )
        if (m) {
          d = new Date(
            parseInt(m[1]),
            parseInt(m[2], 10) - 1,
            parseInt(m[3], 10),
            parseInt(m[4], 10),
            parseInt(m[5], 10),
            parseInt(m[6], 10)
          )
        }
      }
      if (isNaN(d.getTime())) return String(val)
      function pad(n) {
        return n < 10 ? '0' + n : String(n)
      }
      var DD = pad(d.getDate())
      var MM = pad(d.getMonth() + 1)
      var YYYY = d.getFullYear()
      var hh = pad(d.getHours())
      var mm = pad(d.getMinutes())
      var ss = pad(d.getSeconds())
      return DD + '-' + MM + '-' + YYYY + ' ' + hh + ':' + mm + ':' + ss
    },
    twoWords(val) {
      const s = typeof val === 'string' ? val.trim() : ''
      if (!s) return 'Shift -'
      const parts = s.split(/\s+/)
      return parts.slice(0, 2).join(' ')
    },
    toggleDetailList() {
      this.showDetailList = !this.showDetailList
      if (this.showDetailList) {
        // when opening detail list, if not loaded yet, trigger load
        if (!this.printedList || this.printedList.length === 0) {
          this.loadPrintedList()
        }
      }
    },
    async confirmAllPasset() {
      const { isConfirmed } =
        (await this.showConfirmation(
          'Confirm semua passet ini?',
          'Data yang terupdate tidak dapat dikembalikan',
          'Lanjutkan',
          'Batal'
        )) || {}

      if (!isConfirmed) return

      this.loading = true
      const url = `${this.passedApi}palletorder/confirm_all`
      console.log('confirmAllPasset', this.detailData)

      const body = {
        production_id: this.detailData.id,
        status: 1
      }
      try {
        const response = await axios.post(url, body)
        const payload = response && response.data
        if (payload && payload.status_code === '00') {
          this.$swal.fire('Success', payload.status_msg, 'success')
          this.getDetailMO()
        } else {
          const message =
            (payload && payload.status_msg) || 'Gagal confirm data'
          this.$swal.fire('Info', message, 'warning')
        }
        this.loading = false
      } catch (error) {
        this.loading = false
        console.error('saveLoading error', error)
        this.$swal.fire('Error', 'Gagal confirm data', 'error')
      }
    },
    getDetailMO() {
      // Ambil ID dari query params kemudian GET ke API detail
      const id = this.$route && this.$route.query ? this.$route.query.id : null
      if (!id) {
        console.warn('MO id not found in route query')
        return
      }
      const url = `${this.odooApi}manufacture/detail/${id}`
      const self = this
      self.loading = true
      axios
        .get(url)
        .then(function (res) {
          if (res && res.data) {
            if (res.data.status_code === '00') {
              self.detailData = res.data.data
              // populate pallet layers immediately (no need to open dialog)
              self.loadPalletDropdown()
              // load printed list tied to this MO
              self.loadPrintedList()
              self.loadSummaryPallet()
            } else {
              self.detailData = null
              if (self.$swal) {
                self.$swal.fire(
                  'Info',
                  res.data.status_msg || 'Data tidak tersedia',
                  'info'
                )
              }
            }
          }
          self.loading = false
        })
        .catch(function (err) {
          console.error('getDetailMO error', err)
          if (self.$swal) {
            self.$swal.fire('Error', 'Gagal mengambil detail MO', 'error')
          }
          self.loading = false
        })
    },
    goBack() {
      if (this.$router) this.$router.back()
    },
    refreshData() {
      this.showDetailList = false
      this.getDetailMO()
    },
    toYMD(val) {
      if (!val) return null
      var s = String(val)
      if (s.length >= 10) return s.substring(0, 10)
      try {
        var d = new Date(val)
        if (!isNaN(d.getTime())) {
          var mm = d.getMonth() + 1
          var dd = d.getDate()
          return (
            d.getFullYear() +
            '-' +
            (mm < 10 ? '0' + mm : mm) +
            '-' +
            (dd < 10 ? '0' + dd : dd)
          )
        }
      } catch (e) {}
      return null
    },
    getIdSafe(obj, alt) {
      if (!obj && alt != null) return alt
      if (obj && typeof obj === 'object') {
        if (obj.id != null) return obj.id
        if (obj.machine_id != null) return obj.machine_id
        if (obj.shift_id != null) return obj.shift_id
        if (obj.product_id != null) return obj.product_id
      }
      return obj || null
    },
    loadPalletDropdown() {
      console.log('loadPalletDropdown', this.detailData)
      if (!this.detailData || !this.getUserProfile) return
      var mesinId = this.getIdSafe(
        this.detailData.machine,
        this.detailData.machine_id
      )
      console.log('mesinId', mesinId)
      if (!mesinId) return
      var url = `${this.odooApi}master/universal/mrp_mesin_information/dropdown`
      var filter = `[{"mesin_id":${mesinId}}]`
      var join =
        '[{"name":"v_product_product_product_template","alias":"product"},{"name":"palletizer_layer","alias":"palletizer_layer"}]'
      url = url + `?filter=${filter}&join_table=${join}`
      var self = this
      axios
        .get(url)
        .then(function (res) {
          if (res && res.data && res.data.status_code === '00') {
            var arr = res.data.data || []
            console.log('loadPalletDropdown', arr)
            // Filter by product_id matching detail MO
            var targetProductId = self.getIdSafe(
              self.detailData.product,
              self.detailData.product_id
            )
            if (targetProductId != null) {
              arr = arr.filter(function (it) {
                var p = it.product || {}
                return (
                  p.product_id === targetProductId ||
                  it.product_id === targetProductId
                )
              })
            }
            var layers = []
            for (var i = 0; i < arr.length; i++) {
              var pl = arr[i].pallet_layer || arr[i].palletizer_layer
              if (Array.isArray(pl)) {
                for (var j = 0; j < pl.length; j++) layers.push(pl[j])
              } else if (pl) {
                layers.push(pl)
              }
            }
            // Unique by id or JSON
            var seen = {}
            var items = []
            for (var k = 0; k < layers.length; k++) {
              var it = layers[k]
              var key = it.id != null ? 'id_' + it.id : JSON.stringify(it)
              if (seen[key]) continue
              seen[key] = true
              var label = it.name
              if (!label) {
                var a = it.palletizer_qty_per_layer
                var b = it.palletizer_total_layer
                if (a != null && b != null) label = a + ' x ' + b
                else label = 'Pallet '
              }
              var aNum = parseInt(it.palletizer_qty_per_layer, 10)
              var bNum = parseInt(it.palletizer_total_layer, 10)
              items.push({
                name: label,
                id: it.id != null ? it.id : label,
                palletizer_qty_per_layer: isNaN(aNum) ? null : aNum,
                palletizer_total_layer: isNaN(bNum) ? null : bNum
              })
            }
            self.palletTypes = items
            // store unique pallet layers for estimation list
            self.palletLayers = []
            var seenList = {}
            for (var m = 0; m < layers.length; m++) {
              var it2 = layers[m]
              var key2 = it2.id != null ? 'id_' + it2.id : JSON.stringify(it2)
              if (seenList[key2]) continue
              seenList[key2] = true
              self.palletLayers.push(it2)
            }
          } else {
            self.palletTypes = []
          }
        })
        .catch(function (err) {
          console.log('loadPalletDropdown error', err)
          self.palletTypes = []
        })
    },
    mapPrintedStatus(n) {
      var x = typeof n === 'string' ? parseInt(n, 10) : n
      if (x === 0) return 'printed'
      if (x === 1) return 'confirmed'
      if (x === -1) return 'canceled'
      return 'printed'
    },
    loadPrintedList() {
      if (!this.detailData || !this.detailData.id) return
      var url = `${this.passedApi}palletorder/list?production_id=${this.detailData.id}&limit=all&offset=0`
      if (this.searchQuery != null && this.searchQuery != '') {
        url = url + `&keyword=${this.searchQuery}`
      }
      const self = this
      self.listLoading = true
      axios
        .get(url)
        .then(function (res) {
          if (res && res.data && res.data.status_code === '00') {
            var arr = Array.isArray(res.data.data) ? res.data.data : []
            self.printedList = arr.map(function (it) {
              var a = parseInt(it.pallet_qty_per_layer, 10)
              var b = parseInt(it.pallet_total_layer, 10)
              return {
                id: it.id,
                code: it.passed_no || '-',
                printed_at: self.formatDateTime(it.createdAt),
                pallet_config:
                  !isNaN(a) && !isNaN(b)
                    ? a + ' x ' + b
                    : it.pallet_config || '-',
                pallet_qty_per_layer: isNaN(a) ? null : a,
                pallet_total_layer: isNaN(b) ? null : b,
                qty_production:
                  it.qty_production != null ? Number(it.qty_production) : 0,
                status: self.mapPrintedStatus(it.status),
                count: it.printed_count != null ? it.printed_count : 0,
                qty_remain: it.qty_remain != null ? Number(it.qty_remain) : 0,
                lot_no: it.lot_no != null ? it.lot_no : 0
              }
            })
          } else {
            self.printedList = []
          }
          self.listLoading = false
        })
        .catch(function (err) {
          console.error('loadPrintedList error', err)
          self.printedList = []
          self.listLoading = false
        })
    },
    loadSummaryPallet() {
      if (!this.detailData || !this.detailData.id) return
      var url = `${this.passedApi}palletorder/get_summary_pallet?production_id=${this.detailData.id}`
      const self = this
      axios
        .get(url)
        .then(function (res) {
          if (res && res.data && res.data.status_code === '00') {
            self.summaryPallet = Array.isArray(res.data.data)
              ? res.data.data
              : []
          } else {
            self.summaryPallet = []
          }
        })
        .catch(function (err) {
          console.error('loadSummaryPallet error', err)
          self.summaryPallet = []
        })
    },
    openPrintDialog(val) {
      this.dialogPrint = true
      this.printFormError = ''
      // Set default qty based on selected pallet type (if any)
      this.$nextTick(() => {
        if (this.printForm && this.printForm.type != null) {
          this.onPalletTypeChange(this.printForm.type)
        }
      })
    },
    closeDialog() {
      this.dialogPrint = false
      // reset form
      this.printForm = { type: null, qty_print: null, qty_load: null }
    },
    openRejectScanDialog() {
      this.dialogSearchScan = false
      this.dialogRejectScan = true
      const self = this
      this.$nextTick(function () {
        try {
          const el = self.$el.querySelector('input')
          if (el) el.focus()
        } catch (e) {}
      })
    },
    closeRejectScanDialog() {
      this.dialogRejectScan = false
    },
    openSearchScanDialog() {
      this.dialogRejectScan = false
      this.dialogSearchScan = true
    },
    handleSearchScanned(code) {
      const kw = (code || '').toString().trim()
      this.searchFromScanner = true
      this.searchQuery = kw
      if (!this.showDetailList) this.showDetailList = true
    },
    openReprintDialog(row) {
      this.reprintFormError = ''
      this.reprintForm = { id: row && row.id ? row.id : null, qty_print: null }
      this.dialogReprint = true
    },
    confirmReprint() {
      if (
        !this.reprintForm ||
        !this.reprintForm.id ||
        !this.reprintForm.qty_print
      ) {
        this.reprintFormError = 'Lengkapi qty print.'
        return
      }
      this.reprintFormError = ''
      const payload = {
        id: this.reprintForm.id,
        qty_print: this.reprintForm.qty_print
      }
      const self = this
      this.loading = true
      axios
        .post(self.passedApi + 'palletorder/print_passed_by_id', payload)
        .then(function (res) {
          if (res && res.data && res.data.status_code === '00') {
            self.loadPrintedList && self.loadPrintedList()
            self.dialogReprint = false
            self.reprintForm = { id: null, qty_print: null }
            self.$swal &&
              self.$swal.fire('Sukses', 'Reprint berhasil diproses.', 'success')
          } else {
            self.reprintFormError =
              (res && res.data && res.data.status_msg) || 'Gagal reprint.'
          }
        })
        .catch(function (err) {
          console.error('confirmReprint error', err)
          self.reprintFormError = 'Terjadi kesalahan saat memproses reprint.'
        })
        .finally(function () {
          self.loading = false
        })
    },
    handleRejectScanned(code) {
      const passetNo = (code || '').toString().trim()
      if (!passetNo) {
        this.$swal && this.$swal.fire('Info', 'Kode tidak valid.', 'info')
        return
      }
      // Cek apakah kode ada pada printedList
      var idx = -1
      for (var i = 0; i < (this.printedList || []).length; i++) {
        if ((this.printedList[i].code || '').toString().trim() === passetNo) {
          idx = i
          break
        }
      }
      if (idx === -1) {
        this.$swal &&
          this.$swal.fire(
            'Warning',
            'Kode tidak ditemukan pada daftar passet.',
            'warning'
          )
        return
      }

      const url = `${this.passedApi}palletorder/reject/${encodeURIComponent(
        passetNo
      )}`
      const self = this
      self.listLoading = true
      axios
        .put(url)
        .then(function (res) {
          if (res && res.data && res.data.status_code === '00') {
            if (self.$set) {
              self.$set(self.printedList[idx], 'status', 'canceled')
            } else {
              self.printedList[idx].status = 'canceled'
            }
            // Close reject scanner; do not change search filter here
            self.dialogRejectScan = false
            self.$swal &&
              self.$swal.fire(
                'Sukses',
                'Form pallet berhasil direject.',
                'success'
              )
          } else {
            var msg =
              (res && res.data && res.data.status_msg) || 'Gagal reject passet.'
            self.$swal && self.$swal.fire('Warning', msg, 'warning')
          }
        })
        .catch(function (err) {
          console.error('reject passet error', err)
          self.$swal &&
            self.$swal.fire(
              'Error',
              'Terjadi kesalahan saat reject passet.',
              'error'
            )
        })
        .finally(function () {
          self.listLoading = false
        })
    },
    confirmPrint() {
      // Simple validation with inline alert
      if (
        !this.printForm.type ||
        !this.printForm.qty_print ||
        !this.printForm.qty_load
      ) {
        this.printFormError =
          'Lengkapi jenis palet, jumlah muatan dan qty print.'
        return
      }
      this.printFormError = ''
      console.log(this.printForm.type)
      console.log('this.detailData >>>>', this.detailData)
      const xForm = {
        // machine_id: this.detailData.mesin_id,
        // product_id: this.detailData.product_id,
        // shift_id: this.detailData.shift_id,
        manufacture_id: this.detailData.id,
        pallet_layer_id:
          this.printForm.type != null ? this.printForm.type.id : null,
        pallet_qty_per_layer:
          this.printForm.type != null
            ? this.printForm.type.palletizer_qty_per_layer
            : null,
        pallet_total_layer:
          this.printForm.type != null
            ? this.printForm.type.palletizer_total_layer
            : null,
        total_qty_in_pallet: this.printForm.qty_load,
        qty_print: this.printForm.qty_print
        // date: this.detailData.date_planned_start.split('T')[0]
      }
      console.log('xForm >>>>', xForm)
      axios
        .post(`${this.passedApi}palletorder/print_passed`, xForm)
        .then((res) => {
          if (res.data.status_code == '00') {
            this.getDetailMO()
            this.printForm = { type: null, qty_print: null, qty_load: null }
            this.$swal &&
              this.$swal.fire(
                'Sukses',
                'Palet berhasil diproses untuk dicetak.',
                'success'
              )
            this.dialogPrint = false
          } else {
            // show warning inline if possible, fallback to swal
            this.printFormError =
              res.data.status_msg || 'Gagal memproses cetak palet.'
          }
          this.loading = false
        })
        .catch((err) => {
          console.log(err)
          this.loading = false
          this.printFormError = 'Terjadi kesalahan saat memproses permintaan.'
        })
    },
    drawBackground() {
      const canvas = this.$refs.bgCanvas
      if (!canvas) return
      const container = canvas.parentElement
      const w = container.clientWidth
      var h = 220
      if (w < 600) {
        h = 160
      }
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d')
      // clear
      ctx.clearRect(0, 0, w, h)
      // soft blobs using gradients (match palette)
      const g1 = ctx.createRadialGradient(
        w * 0.2,
        h * 0.4,
        10,
        w * 0.2,
        h * 0.4,
        180
      )
      g1.addColorStop(0, 'rgba(144, 202, 249, 0.35)')
      g1.addColorStop(1, 'rgba(144, 202, 249, 0)')
      ctx.fillStyle = g1
      ctx.beginPath()
      ctx.arc(w * 0.2, h * 0.5, 160, 0, Math.PI * 2)
      ctx.fill()

      const g2 = ctx.createRadialGradient(
        w * 0.75,
        h * 0.2,
        10,
        w * 0.75,
        h * 0.2,
        160
      )
      g2.addColorStop(0, 'rgba(165, 214, 167, 0.35)')
      g2.addColorStop(1, 'rgba(165, 214, 167, 0)')
      ctx.fillStyle = g2
      ctx.beginPath()
      ctx.arc(w * 0.75, h * 0.2, 140, 0, Math.PI * 2)
      ctx.fill()
    }
  }
}
</script>

<style scoped>
.width-80 {
  width: 80%;
}
/* Background to match dialog */
.mo-bg {
  background: linear-gradient(180deg, #fafcff 0%, #ffffff 70%);
}

.mo-toolbar {
  background: linear-gradient(135deg, #90caf9 0%, #a5d6a7 100%);
}

.mo-panel {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
}

.bg-canvas {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 220px;
  pointer-events: none;
}

/* Labels and values */
.label {
  color: #6b7280; /* gray-500 */
  font-size: 0.85rem;
  margin-bottom: 2px;
}
/* Header: company centered, status at right */
.mo-header {
  text-align: center;
  position: relative;
  min-height: 28px;
  padding-top: 2px;
  margin-bottom: 8px;
  letter-spacing: 0.5px;
  color: #37474f;
}
.label.with-icon {
  display: flex;
  align-items: center;
}
.label-icon {
  margin-right: 6px;
  color: #64b5f6; /* soft primary accent */
}
.value {
  color: #263238;
  font-size: 1rem;
}
.value.emphasis-1 {
  font-size: 1.25rem;
  font-weight: 700;
}
.value.emphasis-2 {
  font-size: 1.15rem;
  font-weight: 600;
}
.value.strong {
  font-weight: 700;
}
.value.uppercase {
  text-transform: uppercase;
}
.value.big {
  font-size: 1.125rem;
  font-weight: 500;
}
.value.small {
  font-size: 0.9rem;
  color: #546e7a;
}

/* Visual list helpers */
.list-line {
  display: flex;
  align-items: center;
  padding-left: 0.3rem;
}
.list-bullet {
  color: #000000 !important;
}
.list-hint {
  font-size: 0.85rem;
}
.value.small {
  font-size: 0.8rem;
  font-weight: 400;
}

/* Mobile responsiveness */
@media (max-width: 600px) {
  .bg-canvas {
    height: 160px;
  }
  .label {
    font-size: 0.8rem;
  }
  .value {
    font-size: 0.95rem;
  }
  .value.emphasis-1 {
    font-size: 1.15rem;
  }
  .value.emphasis-2 {
    font-size: 1.05rem;
  }
  .value.big {
    font-size: 1.05rem;
  }
}

/* Shift chip styling to match list */
.mo-shift {
  font-size: 14px !important;
  line-height: 1.1;
  padding: 6px 12px !important;
  border-radius: 9999px !important;
  background: linear-gradient(135deg, #64b5f6 0%, #81c784 100%) !important;
  color: #fff !important;
}

/* Primary outlined button (reuse style from dashboard) */
.mo-primary-outline {
  background: #fff !important;
  color: #1e88e5 !important;
  border: 1.5px solid #64b5f6 !important;
  border-radius: 10px;
  box-shadow: 0 3px 12px rgba(100, 181, 246, 0.25),
    0 1px 2px rgba(0, 0, 0, 0.04) !important;
  text-transform: none;
}
.mo-primary-outline .v-icon {
  color: #1e88e5 !important;
}
.mo-primary-outline:hover {
  box-shadow: 0 6px 16px rgba(100, 181, 246, 0.3), 0 2px 6px rgba(0, 0, 0, 0.05) !important;
  background: #f7fbff !important;
}

/* Ensure long button labels don't overflow; show tooltip for full text */
.btn-ellipsis .v-btn__content {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Refresh spin animation */
.spin {
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.mo-wait {
  font-size: 0.95rem;
  color: #455a64;
}
/* Printed list styles */
.search-input .v-input__slot {
  border-radius: 9999px !important;
}
.passet-card {
  border: 1px solid rgba(0, 0, 0, 0.08);
}
.passet-card .code {
  font-weight: 800;
  font-size: 1.05rem;
}
.passet-card .meta {
  font-size: 0.9rem;
  color: #607d8b;
}
.passet-card .v-card__text {
  position: relative;
}
.count-badge {
  position: absolute;
  right: 10px;
  bottom: 6px;
  font-size: 10px;
  color: #607d8b;
}
.passet-card.ok {
  border-left: 3px solid #81c784;
}
.passet-card.warn {
  border-left: 3px solid #ffb74d;
}
.passet-card.bad {
  border-left: 3px solid #e57373;
}
.ok-icon {
  color: #43a047 !important;
}
.warn-icon {
  color: #fb8c00 !important;
}
.bad-icon {
  color: #e53935 !important;
}
/* Summary table */
.summary-wrap {
  border: 1px solid rgba(144, 202, 249, 0.35);
  border-radius: 10px;
  overflow: hidden;
  background: linear-gradient(180deg, #fafcff 0%, #ffffff 70%);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
}
.summary-table thead th {
  background: linear-gradient(
    135deg,
    rgba(144, 202, 249, 0.25) 0%,
    rgba(165, 214, 167, 0.25) 100%
  );
  font-weight: 600;
  color: #37474f;
  text-transform: none;
}
.summary-table td,
.summary-table th {
  border: 1px solid rgba(144, 202, 249, 0.25);
}
.summary-table tbody tr:nth-child(even) {
  background: #fbfdff;
}
.summary-table tbody tr:hover {
  background: rgba(144, 202, 249, 0.12);
}
/* Expand smoother */
.expand-box {
  overflow: hidden;
  will-change: height, opacity;
}

/* Neuromorphic buttons (consistent with scanner) */
.neo-btn.v-btn {
  --bg: #f3f6fb;
  --hi: rgba(255, 255, 255, 0.95);
  background: var(--bg) !important;
  color: #1e88e5 !important; /* primary text */
  border-radius: 14px !important;
  padding: 10px 18px !important;
  text-transform: none;
  box-shadow: 8px 8px 16px rgba(0, 0, 0, 0.08), -8px -8px 16px var(--hi) !important;
}
.neo-btn.v-btn::before {
  background-color: transparent !important;
}
.neo-btn.v-btn:hover {
  box-shadow: 10px 10px 20px rgba(0, 0, 0, 0.1), -10px -10px 20px var(--hi) !important;
}
.neo-btn.v-btn:active {
  box-shadow: inset 6px 6px 14px rgba(0, 0, 0, 0.08),
    inset -6px -6px 14px var(--hi) !important;
}

/* Neo surface background for panels/rows */
.neo-surface {
  --bg: #f3f6fb;
  background: var(--bg) !important;
}

/* Inline neo alert styling (warning) */
.neo-alert.v-alert {
  background: var(--bg) !important;
  border-color: rgba(251, 38, 0, 0.35) !important; /* soft amber */
  color: #d81b60 !important; /* dark amber text */
  border-radius: 12px !important;
  box-shadow: 6px 6px 12px rgba(0, 0, 0, 0.06),
    -6px -6px 12px rgba(255, 255, 255, 0.95) !important;
}
.neo-alert.v-alert .v-icon {
  color: #d81b60 !important;
}

/* Neuromorphic input styling (pill, inset) */
.neo-input /deep/ .v-input__slot,
.neo-input >>> .v-input__slot {
  background: var(--bg);
  border-radius: 9999px !important;
  border: none !important;
  box-shadow: inset 3px 3px 6px rgba(0, 0, 0, 0.1),
    inset -3px -3px 6px rgba(255, 255, 255, 0.95) !important;
  padding-left: 14px !important;
}
.neo-input /deep/ fieldset,
.neo-input >>> fieldset {
  border: 0 !important;
}
.neo-input /deep/ .v-text-field__details,
.neo-input >>> .v-text-field__details {
  display: none;
}
.neo-input /deep/ .v-input__prepend-inner .v-icon,
.neo-input >>> .v-input__prepend-inner .v-icon {
  color: #9aa4b2 !important;
}
.neo-input.v-input--is-focused /deep/ .v-input__slot,
.neo-input.v-input--is-focused >>> .v-input__slot {
  box-shadow: inset 4px 4px 8px rgba(0, 0, 0, 0.12),
    inset -4px -4px 8px rgba(255, 255, 255, 0.98) !important;
}
</style>
